import { Icon, Paper, Button, Typography, Grid, createTheme, ThemeProvider } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { useRef, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { IContato } from '../../commons/types/empresa/contatos';
import { calcularTempoProjeto } from '../../commons/utils/tempoProjeto';
import { history } from '../../helpers/history';
import { useContatosActions } from '../../states/contatos/contatos.actions';
import generatePDF, { Margin, Resolution } from 'react-to-pdf';
import AssessoradosPDF from './components/AssessoradosPDF';
import { useTranslation } from '../../commons/useTranslasion';
import { esES, ptBR } from '@mui/material/locale';
import { useDateFormatters } from '../../utils/useDateFormatters';

const Assessorados: React.FC = () => {
  const { translate } = useTranslation();
  const { formatarData } = useDateFormatters();
  const lang = process.env["REACT_APP_LANG"] ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }
  const actions = useContatosActions();
  const [contatos, setContatos] = useState<IContato[]>([]);
  const [statusCount, setStatusCount] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const targetRef = useRef<HTMLDivElement>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const columns: GridColDef[] = [
    {
      field: 'nome',
      headerName: translate("nome"),
      width: 200,
    },
    {
      field: 'nomeComanda',
      headerName: translate("programa"),
      width: 200
    },
    {
      field: 'email',
      headerName: translate("email"),
      width: 250,
    },
    {
      field: 'faseComanda',
      headerName: translate("fase"),
      width: 320,
    },
    {
      field: 'statusComanda',
      headerName: translate("status"),
      width: 180,
    },
    {
      field: 'prazo',
      headerName: translate("prazo"),
      width: 100,
      valueGetter: (params: any, row: any) => {
        return calcularTempoProjeto(
          new Date(row.dataInicio),
          new Date(row.dataFim),
        );
      },
    },
    {
      field: 'dataInicio',
      headerName: translate("dataInicio"),
      width: 100,
      valueFormatter: (params: any) => {
        return formatarData(params.value);
      },
    },
    {
      field: 'dataFim',
      headerName: translate("dataFim"),
      width: 100,
      valueFormatter: (params: any) => {
        return formatarData(params.value);
      },
    },
    {
      field: 'responsavelComanda',
      headerName: translate("responsavel"),
      width: 200,
    },
    {
      field: 'acoes',
      headerName: translate("acoes"),
      width: 70,
      renderCell: (params: GridCellParams) => {
        let obj = params.row as IContato;
        return (
          <Icon
            onClick={() => navigateToDetalhes(obj.id.toString())}
            style={{ color: 'gray', cursor: 'pointer' }}
          >
            {translate("visibility")}
          </Icon>
        );
      },
    },
  ];

  const [currentColumns, setCurrentColumns] = useState<GridColDef[]>(columns);

  useEffect(() => {
    actions
      .getContatos()
      .then((data) => {
        if (data && data.contatos) {
          setContatos(data.contatos);
          setStatusCount(data.contador);
        }
      })
      .catch((err) => toast.error(translate("falhaAoBuscarAssessorados")))
      .finally(() => setLoading(false));
  }, []);

  const handleExportPDF = async () => {
    try {
      if (!targetRef.current) {
        throw new Error(translate("conteudoNaoEstaProntoParaExportacao"));
      }

      await generatePDF(() => targetRef.current!, {
        filename: 'STATO_Relatorio.pdf',
        method: 'save',
        resolution: Resolution.NORMAL,
        page: {
          margin: Margin.SMALL,
          format: 'a4',
          orientation: 'landscape'
        },
        canvas: {
          mimeType: 'image/jpeg',
          qualityRatio: 0.98
        },
        overrides: {
          pdf: {
            compress: true,
            unit: 'mm',
            format: 'a4'
          },
          canvas: {
            scale: 1.5,
            useCORS: true,
            logging: false
          }
        }
      });
    } catch (error) {
      console.error(translate("erroAoGerarPdf"), error);
      toast.error(translate("falhaAoGerarPdf"));
    }
  };

  const navigateToDetalhes = (id: string) => {
    history.push(`/assessorados/${id}`);
  };

  return (
    <>
      <div style={{ padding: '0 20px', width: '100%' }}>
        <Button
          variant="contained"
          onClick={handleExportPDF}
          sx={{ margin: '20px' }}
        >
          {translate("exportarPdf")}
        </Button>

        <div style={{ width: '100%', overflow: 'hidden', maxWidth: '100%' }}>
          <PaperList sx={{ minHeight: '120px' }}>
            <Grid container spacing={2} sx={{ padding: 2 }}>
              {Object.entries(statusCount || {}).map(([status, count]) => (
                <Grid item xs={12} sm={6} md={3} key={status}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 1.5,
                      textAlign: 'center',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '6px',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.01)',
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1976d2',
                        fontWeight: 'bold',
                        marginBottom: 0.5
                      }}
                    >
                      {`${count}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem'
                      }}
                    >
                      {status}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </PaperList>

          <PaperList>
            <ThemeProvider theme={createTheme({}, esES)}>
              <DataGrid
                getRowId={(row) => row.id + row.nome}
                rows={contatos}
                columns={currentColumns}
                loading={loading}
                pageSizeOptions={[10, 20, 50, 100, 200]}
                onPaginationModelChange={setPaginationModel}
                onRowClick={(data) => navigateToDetalhes(data.row.id.toString())}
              />
            </ThemeProvider>
          </PaperList>
        </div>
      </div>

      <div 
        style={{ 
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '357mm', 
          height: 'auto',
          backgroundColor: 'white',
          margin: 0,
          padding: 0
        }}
      >
        <div ref={targetRef} style={{ width: '100%', height: '100%' }}>
          <AssessoradosPDF
            contatos={contatos}
            statusCount={statusCount}
            columns={columns.filter(column => column.field !== 'acoes')}
          />
        </div>
      </div>
    </>
  );
};

const PaperList = styled(Paper)`
  margin: 20px;
  width: calc(100% - 40px); // Adjust for margins
  border-radius: 8px !important;
  overflow: hidden; // Prevent content overflow

  @media print {
    margin: 0;
    width: 100%;
    border-radius: 0 !important;
    box-shadow: none !important;
    page-break-inside: avoid;
  }
`;

export default Assessorados;
